import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Droplets, 
  ShieldCheck,
  AlertTriangle, 
  RefreshCw,
  Upload,
  School,
  Heart,
  Accessibility,
  BookOpen,
  Utensils,
  Trash2,
  Thermometer,
  Stethoscope,
  Activity,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Brain
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import WaterSafetyWaterTab from "@/components/WaterSafetyWaterTab";
import WaterSafetyHygieneMdmTab from "@/components/WaterSafetyHygieneMdmTab";
import WaterSafetyHealthTab from "@/components/WaterSafetyHealthTab";
import WaterSafetyInclusionAcademicsTab from "@/components/WaterSafetyInclusionAcademicsTab";
import WaterSafetyInsightsTab from "@/components/WaterSafetyInsightsTab";
import WaterSafetyGuidanceChip from "@/components/WaterSafetyGuidanceChip";
import { useWaterSafetyAiGuidance } from "@/hooks/useWaterSafetyAiGuidance";
import "./InfrastructureDashboard.css";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from "recharts";

import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// KPI Card Component
const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description, size = "default" }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    cyan: "bg-cyan-50 text-cyan-600",
  };

  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className={size === "large" ? "p-6" : "p-4"}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className={`${size === "large" ? "text-3xl" : "text-2xl"} font-bold text-slate-900 tabular-nums`}>
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colors[color]}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Gauge Bar Component
const GaugeBar = ({ value, label, color = "#10b981", showTarget = false }) => {
  const getColor = (val) => {
    if (val >= 90) return "#10b981";
    if (val >= 70) return "#f59e0b";
    return "#ef4444";
  };
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="text-sm font-bold" style={{ color: color || getColor(value) }}>{value}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color || getColor(value) }}
        />
      </div>
      {showTarget && (
        <div className="flex justify-between text-xs text-slate-400">
          <span>0%</span>
          <span>Target: 100%</span>
        </div>
      )}
    </div>
  );
};

// Status Indicator
const StatusIndicator = ({ status }) => {
  const raw = typeof status === "string" ? status.toLowerCase() : status;
  const normalized =
    raw === "all" || raw === "some" || raw === "yes" ? "yes" : raw === "non_functional" ? "non_functional" : raw === "no" || raw === "" ? "no" : raw;
  const config = {
    yes: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50" },
    no: { icon: XCircle, color: "text-red-500", bg: "bg-red-50" },
    non_functional: { icon: AlertCircle, color: "text-amber-500", bg: "bg-amber-50" },
  };
  
  const cfg = config[normalized] || config.no;
  const Icon = cfg.icon;
  
  return (
    <div className={`p-1 rounded ${cfg.bg}`}>
      <Icon className={`w-4 h-4 ${cfg.color}`} />
    </div>
  );
};

const InfrastructureDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [blockData, setBlockData] = useState([]);
  const [waterDistribution, setWaterDistribution] = useState(null);
  const [hygieneDistribution, setHygieneDistribution] = useState(null);
  const [healthMetrics, setHealthMetrics] = useState([]);
  const [inclusionMetrics, setInclusionMetrics] = useState(null);
  const [highRiskSchools, setHighRiskSchools] = useState([]);
  const [bottomBlocks, setBottomBlocks] = useState([]);
  const [activeTab, setActiveTab] = useState("water");

  const scopeParams = useMemo(() => {
    const p = {};
    if (scope.districtCode?.trim()) p.district_code = scope.districtCode.trim();
    if (scope.districtName?.trim()) p.district_name = scope.districtName.trim();
    if (scope.blockCode?.trim()) p.block_code = scope.blockCode.trim();
    if (scope.blockName?.trim()) p.block_name = scope.blockName.trim();
    if (scope.udiseCode?.trim()) p.udise_code = scope.udiseCode.trim();
    return p;
  }, [scope.districtCode, scope.districtName, scope.blockCode, scope.blockName, scope.udiseCode]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        blockRes,
        waterRes,
        hygieneRes,
        healthRes,
        inclusionRes,
        riskRes,
        bottomRes
      ] = await Promise.all([
        axios.get(`${API}/infrastructure/overview`, { params: scopeParams }),
        axios.get(`${API}/infrastructure/block-wise`, { params: scopeParams }),
        axios.get(`${API}/infrastructure/water-distribution`, { params: scopeParams }),
        axios.get(`${API}/infrastructure/hygiene-distribution`, { params: scopeParams }),
        axios.get(`${API}/infrastructure/health-metrics`, { params: scopeParams }),
        axios.get(`${API}/infrastructure/inclusion-metrics`, { params: scopeParams }),
        axios.get(`${API}/infrastructure/high-risk-schools`, { params: scopeParams }),
        axios.get(`${API}/infrastructure/bottom-blocks`, { params: scopeParams })
      ]);
      
      setOverview(overviewRes.data);
      setBlockData(blockRes.data);
      setWaterDistribution(waterRes.data);
      setHygieneDistribution(hygieneRes.data);
      setHealthMetrics(healthRes.data);
      setInclusionMetrics(inclusionRes.data);
      setHighRiskSchools(riskRes.data);
      setBottomBlocks(bottomRes.data);
    } catch (error) {
      console.error("Error fetching Infrastructure data:", error);
      toast.error("Failed to load infrastructure data");
    } finally {
      setLoading(false);
    }
  }, [scope.version, scopeParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImport = async () => {
    const url = "https://customer-assets.emergentagent.com/job_7d6cbc1e-b567-4fbc-af84-06fa5107bbd4/artifacts/pynxtgs1_3.%20Drinking_Water_Other_Details_AY_25-26.xlsx";
    
    setImporting(true);
    try {
      await axios.post(`${API}/infrastructure/import?url=${encodeURIComponent(url)}`);
      toast.success("Infrastructure data import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 5000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const {
    guidance,
    provider: guidanceProvider,
    loading: guidanceLoading,
    generatedAt: guidanceGeneratedAt,
  } = useWaterSafetyAiGuidance({
    overview,
    blockData,
    bottomBlocks,
    highRiskSchools,
    inclusionMetrics,
    scopeLabel,
    scopeKey: scope.version,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = overview && overview.total_schools > 0;

  const tabItems = [
    { id: "water", label: "Water & Safety", icon: Droplets },
    { id: "hygiene", label: "Hygiene & MDM", icon: Trash2 },
    { id: "health", label: "Health & Safety", icon: Heart },
    { id: "inclusion", label: "Inclusion & Academics", icon: Accessibility },
    { id: "insights", label: "Insights", icon: Brain },
  ];

  return (
    <div className="inf-dashboard animate-fade-in" data-testid="infrastructure-dashboard">
      <section className="inf-page-header">
        <div>
          <h1>Infrastructure & Water Safety Dashboard</h1>
          <p>
            Drinking water, purification, testing, rainwater harvesting and facility safety monitoring • AY
            2025-26 • {scopeLabel}
          </p>
        </div>
        <div className="inf-header-actions">
          {hasData && (
            <WaterSafetyGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
          )}
          <button
            type="button"
            className="inf-ghost-action"
            onClick={handleImport}
            disabled={importing}
            data-testid="import-infra-btn"
          >
            {importing ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
            Import Data
          </button>
          <ExportPanel dashboardName="infrastructure" dashboardTitle="Infrastructure" ghostAction />
          <button type="button" className="inf-primary-action" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </section>

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Droplets className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Infrastructure Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the Drinking Water & Facilities Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-infra-empty-btn">
              {importing ? "Importing..." : "Import Infrastructure Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Dashboard Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="inf-tabs-wrap" aria-label="Infrastructure dashboard sections">
              <nav className="inf-tabs">
                {tabItems.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`inf-tab ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                    data-testid={`tab-${id}`}
                  >
                    <Icon size={17} />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <TabsContent value="water" className="mt-0">
              <WaterSafetyWaterTab
                overview={overview}
                blockData={blockData}
                waterDistribution={waterDistribution}
                bottomBlocks={bottomBlocks}
                guidance={guidance?.water}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                scopeLabel={scopeLabel}
              />
            </TabsContent>

            <TabsContent value="hygiene" className="mt-0">
              <WaterSafetyHygieneMdmTab
                overview={overview}
                blockData={blockData}
                hygieneDistribution={hygieneDistribution}
                guidance={guidance?.hygiene}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                scopeLabel={scopeLabel}
              />
            </TabsContent>

            <TabsContent value="health" className="mt-0">
              <WaterSafetyHealthTab
                overview={overview}
                blockData={blockData}
                healthMetrics={healthMetrics}
                highRiskSchools={highRiskSchools}
                guidance={guidance?.health}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                scopeLabel={scopeLabel}
              />
            </TabsContent>

            <TabsContent value="inclusion" className="mt-0">
              <WaterSafetyInclusionAcademicsTab
                overview={overview}
                blockData={blockData}
                inclusionMetrics={inclusionMetrics}
                guidance={guidance?.inclusion}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                scopeLabel={scopeLabel}
              />
            </TabsContent>
            <TabsContent value="insights" className="mt-0">
              <WaterSafetyInsightsTab
                overview={overview}
                blockData={blockData}
                bottomBlocks={bottomBlocks}
                guidance={guidance?.insights}
                guidanceProvider={guidanceProvider}
                guidanceLoading={guidanceLoading}
                generatedAt={guidanceGeneratedAt}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default InfrastructureDashboard;
